import { v4 as uuid } from 'uuid';
import { Injectable } from '@angular/core';
import { CustomFabricEllipse, CustomFabricIText, CustomFabricRect, CustomFabricText, Pointer } from '../../_data/data';
import { DrawingToolsIconsEnum } from '../../_enums/drawing-tools-icons.enum';
import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { ThemeEnum } from '../../../theme/_enums/theme.enum';
import { Canvas, Ellipse, FabricImage, FabricText, Group, IText, Line, loadSVGFromURL, Rect, Triangle, util } from 'fabric';
import { MnemonicChart } from './mnemonic-chart.class';
import { MnemonicGauge } from './mnemonic-gauge.class';

@Injectable({
    providedIn: 'root',
})
export class FabricShapeFactory {
    createEllipse(canvas: Canvas, pointer: Pointer, theme: ThemeEnum): CustomFabricEllipse {
        const ellipse = new Ellipse({
            left: pointer.x,
            top: pointer.y,
            stroke: theme === ThemeEnum.light ? '#333' : '#fff',
            fill: '',
            originX: 'center',
            originY: 'center',
            rx: 100,
            ry: 100,
            width: 100,
            height: 100,
            selectable: true,
            hasRotatingPoint: false,
            backgroundColor: 'transparent',
            tooltip: null,
            padding: 8,
            objectType: ObjectTypeEnum.ellipse,
            cornerColor: '#3e96fa',
            name: this.getToolsName('ellipse', canvas),
        } as CustomFabricRect) as CustomFabricEllipse;
        ellipse.id = uuid();
        canvas.add(ellipse);
        return ellipse;
    }

    createRectangle(canvas: Canvas, pointer: Pointer, theme: ThemeEnum): CustomFabricRect {
        const rect = new Rect({
            left: pointer.x,
            top: pointer.y,
            stroke: theme === ThemeEnum.light ? '#333' : '#fff',
            fill: null,
            originX: 'center',
            originY: 'center',
            width: 100,
            height: 100,
            selectable: true,
            hasRotatingPoint: false,
            backgroundColor: 'transparent',
            tooltip: null,
            padding: 8,
            cornerColor: '#3e96fa',
            objectType: ObjectTypeEnum.rect,
            name: this.getToolsName('rectangle', canvas),
        } as CustomFabricRect) as CustomFabricRect;
        rect.id = uuid();
        canvas.add(rect);
        return rect;
    }

    createDoubleTriangle(canvas: Canvas, pointer: Pointer, theme: ThemeEnum): any {
        const color = theme === ThemeEnum.light ? '#333' : '#fff';
        const childDefaults = {
            fill: color,
            originX: 'left' as const,
            originY: 'top' as const,
            selectable: false,
            hasRotatingPoint: false,
        };

        const line = new Line([50, 100, 200, 100], {
            ...childDefaults,
            stroke: color,
            strokeWidth: 3,
            left: 0,
            top: 0,
        });

        const leftTriangle = new Triangle({
            ...childDefaults,
            width: 10,
            height: 15,
            left: -15,
            top: 7,
            angle: -90,
        });

        const rightTriangle = new Triangle({
            ...childDefaults,
            width: 10,
            height: 15,
            left: 165,
            top: -4,
            angle: 90,
        });

        const arrow = new Group([line, leftTriangle, rightTriangle], {
            left: pointer.x,
            top: pointer.y,
            angle: 0,
            padding: 8,
            originX: 'left',
            originY: 'top',
            cornerColor: '#3e96fa',
        });

        arrow.set({
            id: uuid(),
            name: this.getToolsName('arrow', canvas),
            objectType: ObjectTypeEnum.group,
        } as any);

        canvas.add(arrow);

        return arrow;
    }

    createTriangle(canvas: Canvas, pointer: Pointer, theme: ThemeEnum): any {
        const color = theme === ThemeEnum.light ? '#333' : '#fff';
        const childDefaults = {
            fill: color,
            originX: 'left' as const,
            originY: 'top' as const,
            selectable: false,
            hasRotatingPoint: false,
        };

        const line = new Line([50, 100, 200, 100], {
            ...childDefaults,
            stroke: color,
            strokeWidth: 3,
            left: 0,
            top: 0,
        });

        const triangle = new Triangle({
            ...childDefaults,
            width: 10,
            height: 15,
            left: 165,
            top: -4,
            angle: 90,
        });

        const arrow = new Group([line, triangle], {
            left: pointer.x,
            top: pointer.y,
            angle: 0,
            padding: 8,
            originX: 'left',
            originY: 'top',
            cornerColor: '#3e96fa',
        });

        arrow.set({
            id: uuid(),
            name: this.getToolsName('arrow', canvas),
            objectType: ObjectTypeEnum.group,
        } as any);

        canvas.add(arrow);

        return arrow;
    }

    createLine(canvas: Canvas, pointer: Pointer, theme: ThemeEnum): any {
        const line = new Line([50, 100, 200, 100], {
            stroke: theme === ThemeEnum.light ? '#333' : '#fff',
            fill: theme === ThemeEnum.light ? '#333' : '#fff',
            strokeWidth: 3,
            left: pointer.x,
            top: pointer.y,
            selectable: false,
            hasRotatingPoint: false,
            tooltip: null,
            id: uuid(),
            angle: 0,
            padding: 8,
            originX: 'center',
            originY: 'center',
            cornerColor: '#3e96fa',
            name: this.getToolsName('line', canvas),
            objectType: ObjectTypeEnum.line,
        } as any);

        canvas.add(line);
    }

    createChart(canvas: Canvas, pointer: Pointer): MnemonicChart {
        const chart = new MnemonicChart({
            left: pointer.x,
            top: pointer.y,
            name: this.getToolsName('chart', canvas),
            tooltip: null,
            id: uuid(),
            objectType: ObjectTypeEnum.chart,
            chart: {
                type: 'line',
            },
        } as any);

        canvas.add(chart);
        return chart;
    }

    createGauge(canvas: Canvas, pointer: Pointer): MnemonicGauge {
        const gauge = new MnemonicGauge({
            left: pointer.x,
            top: pointer.y,
            name: this.getToolsName('gauge', canvas),
            tooltip: null,
            id: uuid(),
            objectType: ObjectTypeEnum.gauge,
            gauge: {
                min: 0,
                max: 100,
                value: 0,
            },
        } as any);

        canvas.add(gauge);
        return gauge;
    }

    createImage(canvas: Canvas, pointer: Pointer, theme: ThemeEnum, image?: DrawingToolsIconsEnum): any {
        if (image !== DrawingToolsIconsEnum.defaultImage) {
            void loadSVGFromURL(image).then(({ objects, options }) => {
                const img: any = util.groupSVGElements(
                    objects.filter((object): object is NonNullable<typeof object> => object !== null),
                    options,
                );
                (img as any).set({
                    top: pointer.y,
                    left: pointer.x,
                    name: this.getToolsName('path', canvas),
                    tooltip: null,
                    objectType: img._objects?.length ? ObjectTypeEnum.group : ObjectTypeEnum.path,
                    id: uuid(),
                    originX: 'center',
                    originY: 'center',
                    src: image,
                    padding: 8,
                    cornerColor: '#3e96fa',
                });

                img.setCoords();
                canvas.calcOffset();
                canvas.add(img);
                canvas.renderAll();
            });
            return;
        }

        void FabricImage.fromURL(image).then((img: any) => {
            img.set({
                top: pointer.y,
                left: pointer.x,
                name: this.getToolsName('image', canvas),
                tooltip: null,
                objectType: ObjectTypeEnum.defaultImage,
                id: uuid(),
                originX: 'center',
                originY: 'center',
                src: image,
                padding: 8,
                cornerColor: '#3e96fa',
            });

            canvas.add(img);
            canvas.renderAll();
        });
    }

    createIText(
        canvas: Canvas,
        opts: {
            content?: string;
            pointer: { x: number; y: number };
        },
        theme: ThemeEnum,
    ): CustomFabricIText {
        const iText = new IText(opts.content ?? this.getToolsName('text', canvas), {
            fill: theme === ThemeEnum.light ? '#333' : '#fff',
            fontSize: 20,
            backgroundColor: '',
            left: opts.pointer.x,
            top: opts.pointer.y,
            selectable: true,
            hasRotatingPoint: false,
            angle: 0,
            originX: 'center',
            originY: 'center',
            fontWeight: 'normal',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            lineHeight: 1.16,
            tooltip: null,
            textAlign: 'center',
            objectType: ObjectTypeEnum.iText,
            name: this.getToolsName('text', canvas),
            padding: 8,
            cornerColor: '#3e96fa',
        } as CustomFabricIText) as CustomFabricIText;
        iText.id = uuid();
        canvas.add(iText);
        return iText;
    }

    createText(
        canvas: Canvas,
        opts: {
            content?: string;
            pointer: { x: number; y: number };
        },
        theme: ThemeEnum,
    ): CustomFabricText {
        const Text = new FabricText(opts.content ?? this.getToolsName('value', canvas), {
            stroke: null,
            fill: theme === ThemeEnum.light ? '#333' : '#fff',
            fontSize: 20,
            left: opts.pointer.x,
            top: opts.pointer.y,
            originX: 'center',
            originY: 'center',
            selectable: true,
            hasRotatingPoint: false,
            backgroundColor: '',
            angle: 0,
            id: uuid(),
            fontWeight: 'normal',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            lineHeight: 1.16,
            isStatusColor: true,
            tooltip: '',
            textAlign: 'center',
            objectType: ObjectTypeEnum.text,
            name: this.getToolsName('value', canvas),
            padding: 8,
            cornerColor: '#3e96fa',
        } as CustomFabricText & { isStatusColor: boolean }) as CustomFabricText;
        canvas.add(Text);
        return Text;
    }

    getToolsName(name: string, canvas: Canvas): string {
        return name + canvas?.getObjects()?.filter((f: any) => f.name?.includes(name))?.length;
    }
}
